import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { ActivityLevel, CreateWasteEntryDto, IsotopeDto, WasteContainerDto, WasteStatus, WasteType } from '@services/dtos'
import { wasteEntrySchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Grid, InputAdornment, IconButton, Tooltip, MenuItem, TextField, Box, Typography } from '@mui/material'
import { createWasteEntry } from '@state/reducers/wasteEntryReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import WasteContainerForm from './WasteContainerForm'
import { getWasteContainers, setWasteContainerFilters } from '@state/reducers/wasteContainerReducer'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { FormHeader } from './FormHeader'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import SyncIcon from '@mui/icons-material/Sync'
import { wasteContainerService } from '@services/wasteContainerService'
import WasteContainerChips from '@components/WasteContainerChips'
import { MemberDto, MemberService } from '@services/api'
import { getNameWithRole } from '@utils/member.utils'

type Props = {
  onClose: () => void
}

const WasteEntryForm: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedContainer, setSelectedContainer] = useState<WasteContainerDto | null>(null)

  const defaultMember = user && 'role' in user ? user as unknown as MemberDto : null
  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const containerRef = React.useRef(new Map<string, WasteContainerDto>())

  const isVolumetric = selectedContainer?.type === WasteType.LIQUID
  const isGravimetric = selectedContainer?.type === WasteType.SOLID || selectedContainer?.type === WasteType.SHARPS
  const needsActivity = selectedContainer?.activityLevel === ActivityLevel.LOW || selectedContainer?.activityLevel === ActivityLevel.HIGH

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateWasteEntryDto>({
    resolver: yupResolver(wasteEntrySchema),
    context: { container: selectedContainer },
    defaultValues: {},
  })

  useEffect(() => {
    if (!selectedContainer) {
      return
    }
    if (needsActivity) {
      setValue('activityRefAt', new Date())
      const isotopes: IsotopeDto[] = selectedContainer.isotopes ?? []
      if (isotopes.length === 1) {
        setValue('isotopeId', isotopes[0].id)
      }
    } else {
      setValue('activityAtRefMbq', undefined)
      setValue('activityRefAt', undefined)
      setValue('isotopeId', undefined)
    }
    if (!isVolumetric) {
      setValue('volumeMl', undefined)
    }
    if (!isGravimetric) {
      setValue('massG', undefined)
    }
  }, [selectedContainer])

  const fetchOpenContainers = async (search: string) => {
    const page = await wasteContainerService.getAll({
      search,
      status: WasteStatus.OPEN,
      limit: 20,
      page: 0,
    })
    const list = page?.datas ?? []
    for (const c of list) {
      containerRef.current.set(c.id, c)
    }
    return list
  }

  const onSubmitForm = async (data: CreateWasteEntryDto) => {
    setIsSubmitting(true)
    dispatch(createWasteEntry(data))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({ message: 'Le déchet a été enregistré avec succès', options: { variant: 'success' } }))
        onClose()
      })
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            message: 'Une erreur est survenue lors de l\'enregistrement',
            options: { variant: 'error' },
          }),
        ),
      )
      .finally(() => setIsSubmitting(false))
  }

  const containerIsotopes: IsotopeDto[] = selectedContainer?.isotopes ?? []

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title="Nouveau déchet"
          submitText="Enregistrer"
        />

        <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
          <Grid item xs={12}>
            <AddWrapperInput
              legend="Conteneur à déchets"
              addTooltipTitle="Ajouter un conteneur"
              dialogContent={(close) => (
                <WasteContainerForm onClose={close} />
              )}
              onAfterClose={() => {
                dispatch(setWasteContainerFilters({ status: WasteStatus.OPEN }))
                dispatch(getWasteContainers())
              }}
              input={
                <Controller
                  name="containerId"
                  control={control}
                  render={({ field: { value: id, onChange } }) => {
                    const selected = id ? (containerRef.current.get(id) ?? null) : null
                    return (
                      <>
                        <ServerAutocomplete<WasteContainerDto>
                          value={selected}
                          fetchService={fetchOpenContainers}
                          onChange={(container) => {
                            onChange(container?.id ?? null)
                            setSelectedContainer(container ?? null)
                          }}
                          getValue={(c) => c?.id ?? null}
                          getOptionLabel={(value) => {
                            const c = value as WasteContainerDto
                            return c ? c.label : ''
                          }}
                          renderOption={(props, c) => (
                            <li {...props} key={c.id}>
                              <Box sx={{ py: 0.25, width: '100%' }}>
                                <Typography variant="body2" fontWeight={400}>
                                  {c.label}
                                </Typography>

                                <WasteContainerChips container={c} />
                              </Box>
                            </li>
                          )}
                          placeholder="Rechercher un conteneur ouvert..."
                          label="Conteneur ouvert"
                          minSearchLength={0}
                          helperText={errors.containerId?.message}
                          error={!!errors.containerId}
                        />
                        {selectedContainer && (<Box className="mt-2">
                          <WasteContainerChips container={selectedContainer} />
                        </Box>)}
                      </>
                    )
                  }}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <InputField
              control={control}
              name="label"
              label="Label"
              type="text"
            />
          </Grid>

          <Grid item xs={12}>
            <InputField
              control={control}
              name="description"
              label="Notes"
              type="text"
              multiline
              rows={1}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ServerAutocomplete<MemberDto>
              defaultValue={defaultMember}
              initialOptions={defaultMember ? [defaultMember] : []}
              value={defaultMember ?? null}
              fetchService={fetchMembers}
              onChange={(value: MemberDto | null) => {
                setValue('collectedById', value?.id)
              }}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => getNameWithRole(value as MemberDto)}
              debounceMs={300}
              placeholder="Rechercher un membre..."
              label="Collecté par"
              helperText={errors.collectedById?.message}
              error={!!errors.collectedById}
              minSearchLength={0}
            />
          </Grid>

          {isGravimetric && (
            <Grid item xs={12} sm={6}>
              <InputField
                control={control}
                name="massG"
                label="Masse (g)"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
          )}

          {isVolumetric && (
            <Grid item xs={12} sm={6}>
              <InputField
                control={control}
                name="volumeMl"
                label="Volume (mL)"
                type="number"
                inputProps={{ min: 0, step: '0.1' }}
              />
            </Grid>
          )}

          {needsActivity && (
            <>
              {containerIsotopes.length > 1 && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="isotopeId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        fullWidth
                        label="Isotope"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!errors.isotopeId}
                        helperText={errors.isotopeId?.message}
                      >
                        {containerIsotopes.map((iso) => (
                          <MenuItem key={iso.id} value={iso.id}>
                            {iso.name} ({iso.code})
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              )}

              {containerIsotopes.length === 1 && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Isotope"
                    value={`${containerIsotopes[0].name} (${containerIsotopes[0].code})`}
                    disabled
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <InputField
                  control={control}
                  name="activityAtRefMbq"
                  label="Activité (MBq)"
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    label="Heure mesure"
                    value={watch('activityRefAt') ? moment(watch('activityRefAt')) : null}
                    onChange={(date) => {
                      if (!date || !date.isValid()) {
                        setValue('activityRefAt', undefined)
                        return
                      }
                      setValue('activityRefAt', date.toDate())
                    }}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.activityRefAt,
                        helperText: errors.activityRefAt?.message,
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Maintenant" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => setValue('activityRefAt', new Date())}
                                >
                                  <SyncIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
        </Grid>
      </form>
    </div>
  )
}

export default WasteEntryForm
